import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

export interface UserPermissions {
  userId: string;
  orgId: string;
  accessibleZones: string[] | null; // null = all zones
  accessibleSites: string[] | null; // null = all sites
  accessibleDeviceTypes: string[] | null; // null = all types
  canViewDevices: boolean;
  canEditDevices: boolean;
  canDeleteDevices: boolean;
  canViewAlarms: boolean;
  canManageAlarms: boolean;
  canViewReports: boolean;
  canExportData: boolean;
  canManageUsers: boolean;
}

interface PermissionContextType {
  permissions: UserPermissions | null;
  loading: boolean;
  error: string | null;
  hasPermission: (permission: keyof UserPermissions) => boolean;
  canAccessZone: (zoneId: string) => boolean;
  canAccessDeviceType: (deviceType: string) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = async () => {
    if (!isAuthenticated || !user?.id || !user?.orgId) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch user permissions from API
      const response = await api.get(`/users/${user.id}/permissions`);
      setPermissions(response.data);
    } catch (err: any) {
      logger.error('Failed to fetch permissions:', err);
      setError(err.message || 'Failed to load permissions');

      // Set default permissions if fetch fails (for backward compatibility)
      setPermissions({
        userId: user.id,
        orgId: user.orgId,
        accessibleZones: null,
        accessibleSites: null,
        accessibleDeviceTypes: null,
        canViewDevices: true,
        canEditDevices: false,
        canDeleteDevices: false,
        canViewAlarms: true,
        canManageAlarms: false,
        canViewReports: true,
        canExportData: false,
        canManageUsers: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [isAuthenticated, user?.id]);

  const hasPermission = (permission: keyof UserPermissions): boolean => {
    if (!permissions) return false;
    const value = permissions[permission];
    return typeof value === 'boolean' ? value : false;
  };

  const canAccessZone = (zoneId: string): boolean => {
    if (!permissions) return false;
    // null means access to all zones
    if (permissions.accessibleZones === null) return true;
    return permissions.accessibleZones.includes(zoneId);
  };

  const canAccessDeviceType = (deviceType: string): boolean => {
    if (!permissions) return false;
    // null means access to all device types
    if (permissions.accessibleDeviceTypes === null) return true;
    return permissions.accessibleDeviceTypes.includes(deviceType);
  };

  const refreshPermissions = async () => {
    await fetchPermissions();
  };

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        loading,
        error,
        hasPermission,
        canAccessZone,
        canAccessDeviceType,
        refreshPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};
